import { PlatformError } from '@djgg/core';
import type { LlmProvider } from './port.js';

/** Rough capability tier: `fast` = cheap/low-latency, `strong` = high-quality. */
export type ModelTier = 'fast' | 'strong';

/** What a model is a sensible default for. Drives {@link ModelRegistry.defaultFor}.
 *  `enrich` = the builder's pre-generation design-brief pass (fast tier — a
 *  short creative expansion, not code). `review` = the builder's cheap
 *  safety-review pass over generated code (fast tier — a bounded classification).
 *  `critic` = the builder's quality-critic pass that scores a built game against
 *  its design + play-trace and names a fix (fast tier — a bounded judgment).
 *  `diagnose` = the builder's agentic failure-diagnosis pass — on a behavioral
 *  gate failure it runs the sim in-sandbox with instrumentation and returns a
 *  root cause + a targeted fix instruction (strong tier — the hardest reasoning
 *  in the pipeline). `suggest` = the builder's post-build studio-UX pass — a
 *  cheap model drafts an example iterate prompt + an example asset prompt in the
 *  game's own language to prefill the studio boxes (fast tier — a short, bounded
 *  creative draft, never fatal). `activity` = the admin dashboard's Activity
 *  Summary — a two-pass analyst→writer read of a whole RANGE of operational
 *  snapshots (strong tier: finding what is actually notable across up to 180
 *  windows is judgment, not extraction, and it runs at most 7× a day). */
export type TaskTag =
  | 'build'
  | 'iterate'
  | 'chat'
  | 'enrich'
  | 'amend'
  | 'review'
  | 'critic'
  | 'diagnose'
  | 'digest'
  | 'activity'
  | 'suggest'
  | 'audit';

/** One data-driven registry entry. */
export interface ModelEntry {
  /** Provider-native model id (what the adapter sends on the wire). */
  id: string;
  provider: LlmProvider;
  /** Human-facing label for the creator's engine picker. */
  displayName: string;
  tier: ModelTier;
  /** Tasks this model is a reasonable default for. */
  taskTags: readonly TaskTag[];
}

/**
 * Seed catalog. Model ids verified against the live provider APIs 2026-07-07,
 * `claude-opus-5` + `gpt-5.6` 2026-07-29
 * (services/builder/scripts/verify-providers.ts calls each one):
 * - Anthropic: `claude-sonnet-5` (default build/iterate), `claude-opus-4-8`
 *   (strong; default for concept analysis), `claude-opus-5` (strong; Opus 4.8's
 *   successor, creator-selectable).
 * - Gemini: `gemini-2.5-flash` (fast, the live-e2e model), `gemini-2.5-pro` (strong).
 * - OpenAI: `gpt-5.4` (strong build alternate), `gpt-5.6` (strong; alias for
 *   gpt-5.6-sol, creator-selectable), `gpt-5.4-mini` (fast; default chat).
 * Superadmin `platformSettings.defaultEngine` overrides these at runtime via
 * {@link ModelRegistry}'s override hook.
 */
export const SEED_MODELS: readonly ModelEntry[] = [
  {
    id: 'claude-sonnet-5',
    provider: 'anthropic',
    displayName: 'Claude Sonnet 5',
    tier: 'fast',
    taskTags: ['build', 'iterate', 'chat', 'enrich', 'amend', 'review', 'critic', 'digest'],
  },
  {
    id: 'claude-opus-4-8',
    provider: 'anthropic',
    displayName: 'Claude Opus 4.8',
    tier: 'strong',
    taskTags: ['build', 'iterate', 'enrich', 'diagnose'],
  },
  {
    id: 'claude-opus-5',
    provider: 'anthropic',
    displayName: 'Claude Opus 5',
    tier: 'strong',
    taskTags: ['build', 'iterate', 'enrich', 'diagnose', 'activity'],
  },
  {
    id: 'gemini-2.5-flash',
    provider: 'gemini',
    displayName: 'Gemini 2.5 Flash',
    tier: 'fast',
    taskTags: ['iterate', 'chat', 'enrich', 'review', 'suggest'],
  },
  {
    id: 'gemini-2.5-pro',
    provider: 'gemini',
    displayName: 'Gemini 2.5 Pro',
    tier: 'strong',
    taskTags: ['build', 'iterate'],
  },
  {
    id: 'gpt-5.4',
    provider: 'openai',
    displayName: 'GPT-5.4',
    tier: 'strong',
    taskTags: ['build', 'iterate'],
  },
  {
    id: 'gpt-5.6',
    provider: 'openai',
    displayName: 'GPT-5.6',
    tier: 'strong',
    taskTags: ['build', 'iterate'],
  },
  {
    id: 'gpt-5.4-mini',
    provider: 'openai',
    displayName: 'GPT-5.4 mini',
    tier: 'fast',
    taskTags: ['iterate', 'chat', 'digest'],
  },
];

/**
 * Per-task default model ids for the seed catalog. The platform ships with
 * Anthropic as the launch default (CREATOR-PLATFORM.md §8: default provider
 * Anthropic, model `claude-sonnet-5`).
 */
export const SEED_TASK_DEFAULTS: Record<TaskTag, string> = {
  // ANTHROPIC-FIRST. Every Anthropic task falls over to its OpenAI equivalent
  // AUTOMATICALLY and per-call when Anthropic is unavailable (out of credits,
  // rate-limited, 5xx) — see FailoverLlm + MODEL_FAILOVER_EQUIVALENTS — and
  // self-heals the instant Anthropic recovers, with no config change. So these
  // are the true intended defaults; the failover is transparent.
  //
  // Rationale (game-quality initiative 2026-07-16; opus-5 switch, llm-review
  // fix plan 2026-08-02): the code-GENERATION path (build/iterate) runs on the
  // strongest Opus lane — opus-5 is opus-4-8's successor at the SAME $5/$25
  // list price, with adaptive thinking on by default (omitting `thinking`
  // enables it; on 4.8 omission meant no thinking at all) and a lower 512-token
  // cache minimum. Concept analysis (enrich) rides the same lane. Bounded
  // classifications (amend/review/critic/digest) on sonnet. Chat is the
  // low-stakes OpenAI lane (kept exercised).
  build: 'claude-opus-5',
  iterate: 'claude-opus-5',
  chat: 'gpt-5.4-mini',
  enrich: 'claude-opus-5',
  amend: 'claude-sonnet-5',
  review: 'claude-sonnet-5',
  critic: 'claude-sonnet-5',
  // The agentic diagnosis pass is the hardest reasoning in the pipeline (read a
  // failing sim, instrument it, find the root cause) — opus, like the code path.
  diagnose: 'claude-opus-5',
  digest: 'claude-sonnet-5',
  // The Activity Summary reasons over a whole range rather than one window, and
  // runs at most once per range per day — the one place where the strongest
  // model is both affordable and worth it.
  activity: 'claude-opus-5',
  // The studio-UX suggestion pass is deliberately the CHEAPEST lane (owner
  // decision 2026-07-23): a short creative draft prefilling an editable box —
  // gemini-2.5-flash, not the Anthropic classification lane. No failover entry
  // (gemini has none); a provider blip just fails the pass open.
  suggest: 'gemini-2.5-flash',
  // The Tier-2 rendered playtest audit judges beat SCREENSHOTS — the gemini
  // vision lane like the asset judge (which it patterns on). Called over the
  // shared geminiRest transport, not the registry-resolved engines; the entry
  // exists so the task tag has one documented home + admin surfaces resolve
  // it. No failover (gemini has none) — a transport failure fails the tier
  // open as `infra-open`.
  audit: 'gemini-2.5-flash',
};

/**
 * A per-task override the platform-settings layer can inject. When present, its
 * value wins over the seed default in {@link ModelRegistry.defaultFor}. Missing
 * keys fall through to the seed default. A superadmin sets these via
 * `platformSettings.global.defaultEngine`.
 */
export type TaskDefaultOverride = Partial<Record<TaskTag, string>>;

/**
 * Cross-provider model equivalence for automatic provider failover
 * ({@link FailoverLlm}). Maps a PRIMARY (Anthropic) model id → the OpenAI model
 * the platform considers its nearest equivalent, so an Anthropic outage can be
 * ridden out per-call with no config change:
 * - `claude-opus-4-8` (strong build/iterate/enrich lane) → `gpt-5.4`
 * - `claude-opus-5`   (creator-selectable strong lane)   → `gpt-5.6`
 * - `claude-sonnet-5`  (amend/review/critic/digest lane) → `gpt-5.4`
 *
 * NOTE sonnet→gpt-5.4 (NOT gpt-5.4-mini): the quality CRITIC runs on the sonnet
 * lane, and eval:critic is 6/6 on gpt-5.4 but FAILS the iterate-drift guard on
 * gpt-5.4-mini (mini doesn't reliably respect the "fairness probes are
 * informational on a cosmetic iterate" framing — the #1 regression protection).
 * The bounded amend/review lanes are low-token, so full gpt-5.4 costs little.
 *
 * Data-driven and typed so a new pairing is one line. A model with NO entry
 * here does NOT fail over — {@link FailoverLlm} rethrows the original error
 * (there is no equivalent to reroute to). Both the key and value are (and must
 * stay) registered {@link SEED_MODELS} ids; {@link failoverEquivalent} is the
 * one reader.
 */
export const MODEL_FAILOVER_EQUIVALENTS: Readonly<Record<string, string>> = {
  'claude-opus-4-8': 'gpt-5.4',
  'claude-opus-5': 'gpt-5.6',
  'claude-sonnet-5': 'gpt-5.4',
};

/**
 * The OpenAI-equivalent model id for a primary model, or `undefined` when the
 * model has no mapping (→ no failover). The single reader of
 * {@link MODEL_FAILOVER_EQUIVALENTS}.
 */
export function failoverEquivalent(model: string): string | undefined {
  return MODEL_FAILOVER_EQUIVALENTS[model];
}

/** Options for constructing a {@link ModelRegistry}. */
export interface ModelRegistryOptions {
  /** Entries to load. Defaults to {@link SEED_MODELS}. */
  entries?: readonly ModelEntry[];
  /** Base per-task defaults. Defaults to {@link SEED_TASK_DEFAULTS}. */
  taskDefaults?: Record<TaskTag, string>;
  /**
   * The platform-settings override hook. Read lazily on each `defaultFor` call
   * so a live settings change takes effect without rebuilding the registry.
   */
  overrideHook?: () => TaskDefaultOverride | undefined;
}

/**
 * Data-driven model catalog. Lookups by id/provider/tier/task, plus a
 * `defaultFor(task)` that honors a `platformSettings`-backed override hook.
 */
export class ModelRegistry {
  private readonly byId: Map<string, ModelEntry>;
  private readonly entries: readonly ModelEntry[];
  private readonly taskDefaults: Record<TaskTag, string>;
  private readonly overrideHook?: () => TaskDefaultOverride | undefined;

  constructor(opts: ModelRegistryOptions = {}) {
    this.entries = opts.entries ?? SEED_MODELS;
    this.taskDefaults = opts.taskDefaults ?? SEED_TASK_DEFAULTS;
    this.overrideHook = opts.overrideHook;
    this.byId = new Map(this.entries.map((e) => [e.id, e]));
    if (this.byId.size !== this.entries.length) {
      throw new PlatformError('validation', 'ModelRegistry: duplicate model id in entries', {
        retryable: false,
      });
    }
  }

  /** All entries (registration order). */
  list(): readonly ModelEntry[] {
    return this.entries;
  }

  /** Look up an entry by id, or undefined. */
  get(id: string): ModelEntry | undefined {
    return this.byId.get(id);
  }

  /** Look up an entry by id, throwing `not-found` when absent. */
  require(id: string): ModelEntry {
    const e = this.byId.get(id);
    if (!e) {
      throw new PlatformError('not-found', `unknown model id '${id}'`, { retryable: false });
    }
    return e;
  }

  /** All entries for a provider. */
  byProvider(provider: LlmProvider): readonly ModelEntry[] {
    return this.entries.filter((e) => e.provider === provider);
  }

  /** All entries in a tier. */
  byTier(tier: ModelTier): readonly ModelEntry[] {
    return this.entries.filter((e) => e.tier === tier);
  }

  /** All entries tagged for a task. */
  forTask(task: TaskTag): readonly ModelEntry[] {
    return this.entries.filter((e) => e.taskTags.includes(task));
  }

  /**
   * The default model entry for a task. Precedence: override hook value (if it
   * names a known model) > seed/base task default. Throws `not-found` if the
   * resolved id isn't a registered entry (a misconfigured override surfaces
   * loudly rather than silently falling back).
   */
  defaultFor(task: TaskTag): ModelEntry {
    const override = this.overrideHook?.()?.[task];
    const id = override ?? this.taskDefaults[task];
    return this.require(id);
  }
}
